import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { GlobalUpgradeModal } from '../components/GlobalUpgradeModal';
import { LoginPage } from '../../features/auth/pages/LoginPage';
import { SignupPage } from '../../features/auth/pages/SignupPage';
import { VerifyEmailPage } from '../../features/auth/pages/VerifyEmailPage';
import { ForgotPasswordPage } from '../../features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../../features/auth/pages/ResetPasswordPage';
import { InvitePasswordResetPage } from '../../features/auth/pages/InvitePasswordResetPage';
import { SharedChatPage } from '../../features/chat/pages/SharedChatPage';
// User dashboard imports
import { CompaniesListPage as UserCompaniesListPage } from '../../features/dashboard_user/pages/CompaniesListPage';
import { CompanyDetailPage as UserCompanyDetailPage } from '../../features/dashboard_user/pages/CompanyDetailsPage';
import { DocumentsPage as UserDocumentsPage } from '../../features/dashboard_user/pages/DocumentsPage';
import { QueryPage as UserQueryPage } from '../../features/dashboard_user/pages/QueryPage';
import { ChatPage as UserChatPage } from '../../features/dashboard_user/pages/ChatPage';

import { HistoryPage as UserHistoryPage } from '../../features/dashboard_user/pages/HistoryPage';
import { EnrichmentPage as UserEnrichmentPage } from '../../features/dashboard_user/pages/EnrichmentPage';
import { UserCompaniesPage } from '../../features/dashboard_user/pages/UserCompaniesPage';
import { BillingPage } from '../../features/dashboard_user/pages/BillingPage';
import { PartnerBillingPage } from '../../features/dashboard_partner/pages/PartnerBillingPage';

// Partner dashboard imports
import { QueryPage as PartnerQueryPage } from '../../features/dashboard_partner/pages/QueryPage';
import { CompaniesListPage as PartnerCompaniesListPage } from '../../features/dashboard_partner/pages/CompaniesListPage';
import { CompanyDetailPage as PartnerCompanyDetailsPage } from '../../features/dashboard_partner/pages/CompanyDetailsPage';
import { DocumentsPage as PartnerDocumentsPage } from '../../features/dashboard_partner/pages/DocumentsPage';
import { ChatPage as PartnerChatPage } from '../../features/dashboard_partner/pages/ChatPage';

import { HistoryPage as PartnerHistoryPage } from '../../features/dashboard_partner/pages/HistoryPage';
import { EnrichmentPage as PartnerEnrichmentPage } from '../../features/dashboard_partner/pages/EnrichmentPage';
import { PartnerProfilePage } from '../../features/dashboard_partner/pages/PartnerProfilePage';
import { PartnerFormSubmissionsPage } from '../../features/dashboard_partner/pages/PartnerFormSubmissionsPage';
import { PartnerCompaniesPage } from '../../features/dashboard_partner/pages/PartnerCompaniesPage';
import { EnrichmentFlowPage } from '../../features/enrichment/pages/EnrichmentFlowPage';
import { WebEnrichmentPage } from '../../features/web_enrichment/pages/WebEnrichmentPage';
import { LinkedInEnrichmentPage } from '../../features/linkedin_enrichment/pages/LinkedInEnrichmentPage';
import { TracxnVerificationPage } from '../../features/tracxn_verification/pages/TracxnVerificationPage';
import { AdminDashboardPage } from '../../features/admin/dashboard/pages/AdminDashboardPage';
import { AdminCompaniesPage } from '../../features/admin/companies/pages/AdminCompaniesPage';
import { CompanyDetailsPage } from '../../features/admin/companies/pages/CompanyDetailsPage';
import { AdminDocumentsPage } from '../../features/admin/documents/pages/AdminDocumentsPage';
import { AdminQueriesPage } from '../../features/admin/queries/pages/AdminQueriesPage';
import { AdminFormsPage } from '../../features/admin/forms/pages/AdminFormsPage';
import { AdminETLTasksPage } from '../../features/admin/etl_tasks/pages/AdminETLTasksPage';
import { AdminUsersPage } from '../../features/admin/dashboard/pages/AdminUsersPage';
import { SystemHealthPage } from '../../features/admin/dashboard/pages/SystemHealthPage';
import { AdminWebEnrichmentPage } from '../../features/web_enrichment/pages/AdminWebEnrichmentPage';
import { AdminLinkedInEnrichmentPage } from '../../features/linkedin_enrichment/pages/AdminLinkedInEnrichmentPage';
import { AdminEnrichmentPage } from '../../features/admin/enrichment/pages/AdminEnrichmentPage';
import { AdminEnrichmentSelectorPage } from '../../features/admin/enrichment/pages/AdminEnrichmentSelectorPage';
import { AdminBillingPage } from '../../features/admin/billing/pages/AdminBillingPage';
import { AdminAIChatPage } from '../../features/admin/dashboard/pages/AIChatPage';
import { AdminCustomFieldsPage } from '../../features/admin/custom_fields/pages/AdminCustomFieldsPage';
import { COLORS } from '../../core/constants/colors';

// Wrapper components for partner/user routing
const DashboardWrapper: React.FC = () => {
  // All users see the newer dashboard via /dashboard/query
  return <Navigate to="/dashboard/query" replace />;
};

const QueryPageWrapper: React.FC = () => {
  const { authState } = useAuth();
  
  if (authState.user?.is_partner) {
    return <PartnerQueryPage />;
  }
  return <UserQueryPage />;
};

const CompaniesListPageWrapper: React.FC = () => {
  const { authState } = useAuth();
  
  if (authState.user?.is_partner) {
    return <PartnerCompaniesListPage />;
  }
  return <UserCompaniesListPage />;
};

const CompanyDetailsPageWrapper: React.FC = () => {
  const { authState } = useAuth();
  
  if (authState.user?.is_partner) {
    return <PartnerCompanyDetailsPage />;
  }
  return <UserCompanyDetailPage />;
};

const DocumentsPageWrapper: React.FC = () => {
  const { authState } = useAuth();
  
  if (authState.user?.is_partner) {
    return <PartnerDocumentsPage />;
  }
  return <UserDocumentsPage />;
};

const ChatPageWrapper: React.FC = () => {
  const { authState } = useAuth();
  
  if (authState.user?.is_partner) {
    return <PartnerChatPage />;
  }
  return <UserChatPage />;
};

const HistoryPageWrapper: React.FC = () => {
  const { authState } = useAuth();
  
  if (authState.user?.is_partner) {
    return <PartnerHistoryPage />;
  }
  return <UserHistoryPage />;
};

const EnrichmentPageWrapper: React.FC = () => {
  const { authState } = useAuth();
  
  if (authState.user?.is_partner) {
    return <PartnerEnrichmentPage />;
  }
  return <UserEnrichmentPage />;
};

const BillingPageWrapper: React.FC = () => {
  const { authState } = useAuth();
  
  if (authState.user?.is_partner) {
    return <PartnerBillingPage />;
  }
  return <BillingPage />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useAuth();
  const location = useLocation();

  if (authState.isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: COLORS.extended.cultured }}
      >
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{
              borderColor: COLORS.primary.violet,
              borderTopColor: 'transparent',
            }}
          ></div>
          <p className="mt-4" style={{ color: COLORS.primary.persianIndigo }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admins are exempt from the partner enrichment gate (mirrors PartnerRoute/AdminRoute)
  const isAdmin = authState.user?.permissions?.is_admin === true ||
    authState.user?.groups?.includes('admin') || false;

  // Members of a company (not its admin/owner) don't do company onboarding —
  // exempt them from the enrichment gate so they aren't bounced to /enrichment.
  const isMemberOnly = authState.user?.has_company_access === true &&
    authState.user?.has_company_admin_access !== true;

  // Only check enrichment redirects for partners (not admins or plain members)
  if (authState.user?.is_partner && !isAdmin && !isMemberOnly) {
    // For partners, we rely on the login redirect logic which checks /api/v1/companies/my-company
    // The ProtectedRoute should only check enrichment_status for redirects
    const localStatus = typeof window !== 'undefined' ? localStorage.getItem('enrichmentStatus') : null;
    const userStatus = authState.user?.enrichment_status || null;
    
    // Only redirect if enrichment_status indicates enrichment is needed
    const needsEnrichment = 
      (userStatus && userStatus !== 'completed') ||
      (!!localStatus && localStatus !== 'completed');

    // Allow access to /dashboard/enrichment and other enrichment routes
    const isEnrichmentRoute = location.pathname.startsWith('/dashboard/enrichment') || 
                              location.pathname.startsWith('/dashboard/web-enrichment') ||
                              location.pathname.startsWith('/dashboard/linkedin-enrichment');

    if (needsEnrichment && location.pathname !== '/enrichment' && !isEnrichmentRoute) {
      return <Navigate to="/enrichment" replace />;
    }

    // If enrichment is completed and partner is on initial enrichment page, redirect to dashboard
    // But allow access to /dashboard/enrichment routes
    if ((userStatus === 'completed' || localStatus === 'completed') && 
        location.pathname === '/enrichment' && 
        !isEnrichmentRoute) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

const PartnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useAuth();
  const location = useLocation();

  if (authState.isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: COLORS.extended.cultured }}
      >
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{
              borderColor: COLORS.primary.violet,
              borderTopColor: 'transparent',
            }}
          ></div>
          <p className="mt-4" style={{ color: COLORS.primary.persianIndigo }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = authState.user?.permissions?.is_admin === true ||
    authState.user?.groups?.includes('admin') || false;

  // Only partners (or admins) can access partner routes
  if (!authState.user?.is_partner && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect partners without company to enrichment (admins are exempt)
  // But allow access to partner companies page and enrichment routes
  const allowedRoutesWithoutCompany = [
    '/dashboard/partner/companies',
    '/enrichment',
    '/dashboard/enrichment',
    '/dashboard/web-enrichment',
    '/dashboard/linkedin-enrichment'
  ];

  // A partner "has a company" if they administer/belong to one — partners have no
  // top-level company_id, so also honour has_company_admin_access / administered_companies.
  const hasCompany =
    !!authState.user?.company_id ||
    authState.user?.has_company_admin_access === true ||
    (authState.user?.administered_companies?.length ?? 0) > 0 ||
    authState.user?.has_company_access === true ||
    (authState.user?.member_companies?.length ?? 0) > 0;

  if (!isAdmin && !hasCompany && !allowedRoutesWithoutCompany.includes(location.pathname)) {
    return <Navigate to="/enrichment" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useAuth();

  if (authState.isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: COLORS.extended.cultured }}
      >
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{
              borderColor: COLORS.primary.violet,
              borderTopColor: 'transparent',
            }}
          ></div>
          <p className="mt-4" style={{ color: COLORS.primary.persianIndigo }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated and has admin role
  if (!authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check for admin permissions or admin group
  const isAdmin = authState.user?.permissions?.is_admin === true ||
    authState.user?.groups?.includes('admin') || false;

  return isAdmin ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

export const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite-password-reset" element={<InvitePasswordResetPage />} />
        {/* Public shared-chat view — no auth, no layout chrome */}
        <Route path="/shared/:token" element={<SharedChatPage />} />
        <Route
          path="/enrichment"
          element={
            <ProtectedRoute>
              <EnrichmentFlowPage />
            </ProtectedRoute>
          }
        />

        {/* Main dashboard - different for partners vs users */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/query"
          element={
            <ProtectedRoute>
              <QueryPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/chat"
          element={
            <ProtectedRoute>
              <ChatPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/chat/:conversationId"
          element={
            <ProtectedRoute>
              <ChatPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/ai-assistant"
          element={<Navigate to="/dashboard/chat" replace />}
        />

        <Route
          path="/dashboard/companies"
          element={
            <ProtectedRoute>
              <CompaniesListPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/companies/:id"
          element={
            <ProtectedRoute>
              <CompanyDetailsPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/documents"
          element={
            <ProtectedRoute>
              <DocumentsPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/history"
          element={
            <ProtectedRoute>
              <HistoryPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/enrichment"
          element={
            <ProtectedRoute>
              <EnrichmentPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/enrichment/:companyId"
          element={
            <ProtectedRoute>
              <EnrichmentPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/partner/profile"
          element={
            <PartnerRoute>
              <PartnerProfilePage />
            </PartnerRoute>
          }
        />

        <Route
          path="/dashboard/partner/form-submissions"
          element={
            <PartnerRoute>
              <PartnerFormSubmissionsPage />
            </PartnerRoute>
          }
        />

        <Route
          path="/dashboard/partner/companies"
          element={
            <PartnerRoute>
              <PartnerCompaniesPage />
            </PartnerRoute>
          }
        />

        <Route
          path="/dashboard/partner/ai-assistant"
          element={<Navigate to="/dashboard/chat" replace />}
        />

        <Route
          path="/dashboard/user/companies"
          element={
            <ProtectedRoute>
              <UserCompaniesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/billing"
          element={
            <ProtectedRoute>
              <BillingPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/web-enrichment"
          element={
            <ProtectedRoute>
              <WebEnrichmentPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/linkedin-enrichment"
          element={
            <ProtectedRoute>
              <LinkedInEnrichmentPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/companies"
          element={
            <AdminRoute>
              <AdminCompaniesPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/companies/:id"
          element={
            <AdminRoute>
              <CompanyDetailsPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/documents"
          element={
            <AdminRoute>
              <AdminDocumentsPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/queries"
          element={
            <AdminRoute>
              <AdminQueriesPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/forms"
          element={
            <AdminRoute>
              <AdminFormsPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/etl-tasks"
          element={
            <AdminRoute>
              <AdminETLTasksPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsersPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/system-health"
          element={
            <AdminRoute>
              <SystemHealthPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/web-enrichment"
          element={
            <AdminRoute>
              <AdminWebEnrichmentPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/linkedin-enrichment"
          element={
            <AdminRoute>
              <AdminLinkedInEnrichmentPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/enrichment"
          element={
            <AdminRoute>
              <AdminEnrichmentSelectorPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/enrichment/:companyId"
          element={
            <AdminRoute>
              <AdminEnrichmentPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/billing"
          element={
            <AdminRoute>
              <AdminBillingPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/ai-assistant"
          element={
            <AdminRoute>
              <AdminAIChatPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/tracxn-verification"
          element={
            <AdminRoute>
              <TracxnVerificationPage />
            </AdminRoute>
          }
        />

        <Route
          path="/admin/custom-fields"
          element={
            <AdminRoute>
              <AdminCustomFieldsPage />
            </AdminRoute>
          }
        />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <GlobalUpgradeModal />
    </BrowserRouter>
  );
};
